import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import Context from "./Context";
// Token hết hạn: gọi API qua fetchXacThuc — tự làm mới token và chạy lại; không cứu được thì mở form đăng nhập.
import AuthContext from "../auth/Context";
import fetchXacThuc from "../auth/fetchXacThuc";

// ── MUI 7 ─────────────────────────────────────────────────────────────────────
import {
  Box, Stack, Typography, Button, IconButton, Chip, Tooltip, Badge,
  CircularProgress, AppBar, Toolbar, alpha
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon      from '@mui/icons-material/StopRounded';
import SaveRoundedIcon      from '@mui/icons-material/SaveRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'; 
import CloseRoundedIcon     from '@mui/icons-material/CloseRounded';
import ViewSidebarRoundedIcon from '@mui/icons-material/ViewSidebarRounded';
import HistoryRoundedIcon   from '@mui/icons-material/HistoryRounded';
import MenuBookRoundedIcon  from '@mui/icons-material/MenuBookRounded';

import Drawer from "../ManagedModal"

// ── ReactFlow ─────────────────────────────────────────────────────────────────
import ReactFlow, {
  addEdge, useNodesState, useEdgesState, Controls, useReactFlow,
  ReactFlowProvider, applyNodeChanges, applyEdgeChanges, Background
} from 'reactflow';
import 'reactflow/dist/style.css';
import './FlowEditor.css';

import debounce  from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';

// Danh sách node KHÔNG còn cứng ở client: nó được đồng bộ với máy chủ (node của gói chưa cài thì
// không hiện; node máy chủ có mà client chưa khai báo thì dùng node động). Xem useFlowNodes/nodeSync.
import useFlowNodes       from "./useFlowNodes";
import Sidebar            from "./SideBar";
import PropertiesBar      from "./PropertiesBar";
import NodeStatusBar      from "./NodeStatusBar";
import { validateFlowData } from "./utils";
import UserformTaskPanel  from "./nodes/UserformTaskPanel";
import FlowNotices        from "./FlowNotices";
import CanvasNodeSearch   from "./CanvasNodeSearch";
import LichSuChay         from "./LichSuChay";
import HuongDanFlow       from "./HuongDanFlow";

// ─────────────────────────────────────────────────────────────────────────────
const snapGrid      = [10, 10];
const defaultViewport = { x: 0, y: 0, zoom: 1 };

const statusColor = (status) => {
  switch (status) {
    case 'running':   case 'listening': return 'success';
    case 'failed':    return 'error';
    case 'stopped':   return 'default';
    default:          return 'info';
  }
};

const FlowEditor = ({ initialFlowId, apiUrl, accessToken, idApp, trustKey, containerSx={}, viewOnly = false }) => {
  const reactFlowWrapper = useRef(null);
  const apis = (useContext(AuthContext) || {}).apis || null;
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const { project, fitView } = useReactFlow();

  const [flowId,         setFlowId]         = useState(initialFlowId || null);
  const [flowTitle,      setFlowTitle]       = useState('');
  const [loading,        setLoading]         = useState(false);
  const [error,          setError]           = useState(null);
  const [flowStatus,     setFlowStatus]      = useState(null);
  const [flowInstanceId, setFlowInstanceId]  = useState(null);
  // Mã instance ĐANG theo dõi (đọc trong callback bất đồng bộ): kết quả hỏi trạng thái của instance CŨ về muộn — vd lượt
  // hỏi lúc vừa bấm Chạy — không được ghi đè trạng thái của instance mới (trước đây làm màn hình nhảy về "Dừng").
  const instanceDangXem = useRef(null);
  useEffect(() => { instanceDangXem.current = flowInstanceId; }, [flowInstanceId]);
  const [isRunning,      setIsRunning]       = useState(false);
  const [selectedNode,   setSelectedNode]    = useState(null);

  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [statusBarOpen,  setStatusBarOpen]  = useState(false);
  // Thanh node bên trái thu gọn được để nhường chỗ cho sơ đồ.
  const [sidebarOpen,    setSidebarOpen]    = useState(true);

  const [taskPanelOpen,  setTaskPanelOpen]   = useState(false);
  const [lichSuOpen,     setLichSuOpen]      = useState(false);
  const [huongDanOpen,   setHuongDanOpen]    = useState(false);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);

  const hasChangesSinceLoad = useRef(false);
  const isInitialLoad       = useRef(false);

  const isLocked = isRunning || viewOnly;
  // Thanh node chỉ có ích khi SOẠN được (thêm node): chỉ xem hoặc đang chạy thì tự ẩn để nhường
  // chỗ cho sơ đồ. Dừng quy trình -> tự hiện lại theo lựa chọn thu gọn/mở của người dùng.
  const showSidebar = !isLocked && sidebarOpen;

  // Node khả dụng THẬT của máy chủ này (đã trộn với khai báo client). Hỏi hỏng -> giữ danh sách client.
  const {
    nodes: nodeDefinition, nodeTypes, nodeTypeMetadata,
    dynamic: dynamicNodeTypes, hidden: hiddenNodeTypes, missingPackages, loading: nodesLoading,
    serverError: nodesServerError,
  } = useFlowNodes({ apiUrl, idApp, accessToken, trustKey, apis });

  // Node trên sơ đồ mà KHÔNG có trong danh sách node đang biết. Không nói ra thì chúng chỉ là những
  // khung trắng không tên — người dùng không biết đó là lỗi hay là cố ý.
  const unknownNodeTypes = useMemo(() => {
    const biet = nodeTypes || {};
    return [...new Set(nodes.map((n) => n.type).filter((t) => t && !biet[t]))];
  }, [nodes, nodeTypes]);

  // Flow ĐÃ LƯU có thể còn node của gói vừa bị gỡ khỏi máy chủ. Node vẫn vẽ ra được (để sửa/xoá) nhưng
  // chạy sẽ hỏng — nói thẳng ra cho người dùng thay vì để họ bấm Chạy rồi nhận lỗi khó hiểu.
  const missingNodeTypes = useMemo(() => {
    if (!hiddenNodeTypes || !hiddenNodeTypes.length) return [];
    const used = new Set(nodes.map((n) => n.type));
    return hiddenNodeTypes.filter((t) => used.has(t))
      .map((t) => (missingPackages && missingPackages[t] ? `${t} (gói ${missingPackages[t]})` : t));
  }, [hiddenNodeTypes, nodes, missingPackages]);

  // type -> định nghĩa node (label, mô tả…) cho ô tìm node trên sơ đồ.
  const nodeDefinitionMap = useMemo(() => {
    const m = {};
    (nodeDefinition || []).forEach((d) => { if (d && d.type) m[d.type] = d; });
    return m;
  }, [nodeDefinition]);

  // Thông báo hiển thị ở dải RIÊNG dưới thanh công cụ (không nhét vào Toolbar nữa — lỗi dài làm bể
  // thanh công cụ). Hiện đồng thời được nhiều thông báo thay vì cái này che cái kia.
  const notices = useMemo(() => {
    const list = [];
    if (error) {
      list.push({ key: 'error', severity: 'error', text: typeof error === 'string' ? error : error.message, onClose: () => setError(null) });
    }
    if (nodesServerError) {
      list.push({
        key: 'nodes-server', severity: 'warning',
        text: `Không lấy được danh sách node từ máy chủ (${nodesServerError}) — đang dùng danh sách có sẵn ở trình duyệt, node của các gói mở rộng sẽ không hiện`
          + (unknownNodeTypes.length ? `. Node chưa nhận diện trên sơ đồ: ${unknownNodeTypes.join(', ')}.` : '.'),
      });
    } else if (missingNodeTypes.length > 0) {
      list.push({
        key: 'nodes-missing', severity: 'warning',
        text: `Máy chủ không có node: ${missingNodeTypes.join(', ')} — cần cài gói mở rộng tương ứng rồi khởi động lại máy chủ.`,
      });
    }
    return list;
  }, [error, nodesServerError, unknownNodeTypes, missingNodeTypes]);

  const latestSelectedNode = useMemo(() => {
    if (!selectedNode) return null;
    return nodes.find(n => n.id === selectedNode.id);
  }, [selectedNode, nodes]);

  const handleNodesStatusUpdate = useCallback((new_nodes) => {
    setNodes(prev => prev.map(node => {
      const found = new_nodes.find(n => n.id === node.id && n.type === node.type);
      return found
        ? { ...node, selected: false, data: { ...node.data, nodeState: { ...found } } }
        : { ...node, selected: false };
    }));
  }, [setNodes]);

  const deleteNodeById = useCallback((nodeId) => {
    if (isLocked) return;
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
    hasChangesSinceLoad.current = true;
    setSelectedNode(prev => prev?.id === nodeId ? null : prev);
  }, [isLocked, setNodes, setEdges]);

  const fetchFlowStatus = useCallback(async () => {
    if (!flowInstanceId) return;
    try {
      const res = await fetchXacThuc(apis,
        `${apiUrl}/api/${idApp}/flow/status/${flowInstanceId}?access_token=${accessToken}&trustkey=${trustKey}`
      );
      if (!res.ok) return;
      const instance = await res.json();
      if (instanceDangXem.current !== flowInstanceId) return;   // đã chuyển sang instance khác trong lúc chờ
      instance.nodes = instance.nodes || [];
      setFlowStatus(instance);
      handleNodesStatusUpdate(instance.nodes);
      setIsRunning(instance.status === 'running' || instance.status === 'listening');
    } catch (e) {
      console.error('[FlowEditor] fetchFlowStatus error:', e);
    }
  }, [flowInstanceId, apiUrl, idApp, accessToken, trustKey, handleNodesStatusUpdate, apis]);

  const loadFlow = useCallback(async (id) => {
    isInitialLoad.current = true;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetchXacThuc(apis, `${apiUrl}/api/${idApp}/flow/${id}?access_token=${accessToken}&trustkey=${trustKey}`);
      if (res.phienKetThuc) return;   // form đăng nhập đã mở — không hiện lỗi token
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const loadedNodes = (data.nodes || []).map(node => ({
        ...node,
        data: { ...node.data}
      }));
      setNodes(loadedNodes);
      setEdges(data.edges || []);
      setFlowId(data._id);
      setFlowTitle(data.title);

      if (data.activeInstance?.instanceId) {
        setFlowInstanceId(data.activeInstance.instanceId);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      isInitialLoad.current       = false;
      hasChangesSinceLoad.current = false;
    }
  }, [apiUrl, idApp, accessToken, trustKey, setNodes, setEdges, apis]);

  useEffect(() => {
    if (initialFlowId) loadFlow(initialFlowId);
  }, [initialFlowId, loadFlow]);

  const onSave = useCallback(async () => {
    if (viewOnly) return; 
    try {
      const _nodes = cloneDeep(nodes).map(n => {
        delete n.data?.nodeState;
        delete n.selected;
        delete n.dragging;
        return n;
      });
      const body = JSON.stringify({ nodes: _nodes, edges });

      if (flowId) {
        await fetchXacThuc(apis,
          `${apiUrl}/api/${idApp}/flow/${flowId}?access_token=${accessToken}&trustkey=${trustKey}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body }
        );
      } else {
        const res  = await fetchXacThuc(apis,
          `${apiUrl}/api/${idApp}/flow?access_token=${accessToken}&trustkey=${trustKey}`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nodes: _nodes, edges, title: 'flow' }) }
        );
        if (res.phienKetThuc) return;
        const data = await res.json();
        setFlowId(data._id);
        setFlowTitle(data.title);
      }
      hasChangesSinceLoad.current = false;
    } catch (e) {
      console.error('[FlowEditor] onSave error:', e);
    }
  }, [viewOnly, flowId, nodes, edges, apiUrl, idApp, accessToken, trustKey, apis]);

  const handleStartFlow = useCallback(async () => {
    if (viewOnly) return;
    if (!flowId) return setError('Bạn cần lưu flow trước khi chạy');
    setError(null);

    const validationErrors = validateFlowData({ nodes }, nodeDefinition);
    if (validationErrors.length > 0) {
      setError(
        'Flow không hợp lệ:\n' +
        validationErrors.map(e => `• ${e.nodeName}: thiếu ${e.missingFields.join(', ')}`).join('\n')
      );
      return;
    }

    // Thôi theo dõi instance CŨ (đã dừng) ngay lúc bấm: nếu không, vòng hỏi trạng thái mỗi giây vẫn hỏi instance cũ trong
    // lúc chờ máy chủ khởi động, nhận "stopped" và tắt cờ đang chạy.
    setFlowInstanceId(null);
    instanceDangXem.current = null;
    setIsRunning(true);
    try {
      const res  = await fetchXacThuc(apis,
        `${apiUrl}/api/${idApp}/flow/start/${flowId}?access_token=${accessToken}&trustkey=${trustKey}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
      );
      if (res.phienKetThuc) { setIsRunning(false); return; }
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || d.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setFlowInstanceId(data.flowInstanceId);
    } catch (e) {
      setIsRunning(false);
      setError(e.message);
    }
  }, [viewOnly, flowId, nodes, apiUrl, idApp, accessToken, trustKey, apis]);

  const handleStopFlow = useCallback(async () => {
    if (viewOnly) return;
    setIsRunning(false);
    try {
      const res = await fetchXacThuc(apis,
        // flow_id: máy chủ dừng CẢ flow (mọi container) kể cả khi mã instance còn rỗng — vd bấm Dừng ngay sau Chạy.
        `${apiUrl}/api/${idApp}/flow/stop/${flowInstanceId || 'none'}?flow_id=${flowId}&access_token=${accessToken}&trustkey=${trustKey}`
      );
      if (res.ok) fetchFlowStatus();
    } catch (e) {
      console.error('[FlowEditor] handleStopFlow error:', e);
    }
  }, [viewOnly, flowId, flowInstanceId, apiUrl, idApp, accessToken, trustKey, fetchFlowStatus, apis]);

  useEffect(() => {
    if (!flowId || !isRunning || !flowInstanceId) return;
    const id = setInterval(fetchFlowStatus, 1000);
    return () => clearInterval(id);
  }, [flowId, isRunning, flowInstanceId, fetchFlowStatus]);

  useEffect(() => {
    if (flowInstanceId) fetchFlowStatus();
  }, [flowInstanceId]);

  // Mở khung thuộc tính + debug cho 1 node. Chế độ CHỈ XEM thì không bao giờ mở.
  const openNodePanels = useCallback((node) => {
    if (viewOnly || !node) return;
    setSelectedNode(node);
    setPropertiesOpen(true);
    setStatusBarOpen(true);
  }, [viewOnly]);

  // Click 1 lần:
  //   - Đang chạy (không chỉ xem): mở ngay khung thuộc tính + debug — sơ đồ đã khoá kéo thả nên
  //     click đơn không còn xung đột với thao tác kéo node.
  //   - Đang soạn: click đơn giữ để chọn/kéo node; nếu khung đã mở thì chuyển sang node vừa click.
  //   - Chỉ xem: không làm gì.
  const onNodeClick = useCallback((_, node) => {
    if (viewOnly) return;
    if (isRunning) return openNodePanels(node);
    if (propertiesOpen || statusBarOpen) setSelectedNode(node);
  }, [viewOnly, isRunning, propertiesOpen, statusBarOpen, openNodePanels]);

  const onNodeDoubleClick = useCallback((_, node) => openNodePanels(node), [openNodePanels]);

  // Chuyển sang chỉ xem thì đóng các khung đang mở.
  useEffect(() => {
    if (viewOnly) { setPropertiesOpen(false); setStatusBarOpen(false); }
  }, [viewOnly]);

  // Tìm node trên sơ đồ -> đưa khung nhìn tới node và tô chọn nó.
  const focusNode = useCallback((node) => {
    if (!node) return;
    setNodes((prev) => prev.map((n) => (n.selected === (n.id === node.id) ? n : { ...n, selected: n.id === node.id })));
    fitView({ nodes: [{ id: node.id }], duration: 400, maxZoom: 1.2, padding: 0.6 });
    if (isRunning && !viewOnly) openNodePanels(node);
    else if (!viewOnly && (propertiesOpen || statusBarOpen)) setSelectedNode(node);
  }, [setNodes, fitView, isRunning, viewOnly, propertiesOpen, statusBarOpen, openNodePanels]);
  
  const onPaneClick = useCallback(() => {
    if (!propertiesOpen && !statusBarOpen) {
      setSelectedNode(null);
    }
  }, [propertiesOpen, statusBarOpen]);
  
  const onNodeDataChange  = useCallback((nodeId, newData) => {
    // Đang chạy: khung thuộc tính chỉ để XEM (sửa lúc này sẽ tự lưu định nghĩa mà instance đang
    // chạy không nhận) — dừng quy trình rồi mới sửa.
    if (isLocked) return;
    hasChangesSinceLoad.current = true;
    setNodes(prev =>
      prev.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n)
    );
  }, [isLocked, setNodes]);

  const onNodesChangeHandler = useCallback((changes) => {
    if (isLocked) return;
    setNodes(prev => applyNodeChanges(changes, prev));
    if (!isInitialLoad.current) {
      const real = changes.some(c =>
        (c.type === 'position' && c.dragging !== false) || c.type === 'add' || c.type === 'remove'
      );
      if (real) hasChangesSinceLoad.current = true;
    }
  }, [isLocked, setNodes]);

  const onEdgesChangeHandler = useCallback((changes) => {
    if (isLocked) return;
    setEdges(prev => applyEdgeChanges(changes, prev));
    if (!isInitialLoad.current) {
      if (changes.some(c => c.type === 'add' || c.type === 'remove')) {
        hasChangesSinceLoad.current = true;
      }
    }
  }, [isLocked, setEdges]);

  const onNodesDelete = useCallback((del) => {
    if (isLocked) return;
    setNodes(prev => prev.filter(n => !del.find(d => d.id === n.id)));
    setEdges(prev => prev.filter(e => !del.some(n => e.source === n.id || e.target === n.id)));
    hasChangesSinceLoad.current = true;
  }, [isLocked, setNodes, setEdges]);

  const onEdgesDelete = useCallback((del) => {
    if (isLocked) return;
    setEdges(prev => prev.filter(e => !del.find(d => d.id === e.id)));
    hasChangesSinceLoad.current = true;
  }, [isLocked, setEdges]);

  const onConnect = useCallback((params) => {
    if (isLocked) return;
    setEdges(eds => addEdge(params, eds));
    if (!isInitialLoad.current) hasChangesSinceLoad.current = true;
  }, [isLocked, setEdges]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    if (!isLocked) e.dataTransfer.dropEffect = 'move';
  }, [isLocked]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    if (isLocked) return;
    const bounds   = reactFlowWrapper.current.getBoundingClientRect();
    const { type, data } = JSON.parse(e.dataTransfer.getData('application/reactflow'));
    const position = project({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });

    setNodes(prev => [
      ...prev, 
      { id: `${type}-${Date.now()}`, type, position, data: { ...data } }
    ]);

    hasChangesSinceLoad.current = true;
  }, [isLocked, project, setNodes]);

  const debouncedSave = useMemo(() => debounce(onSave, 500), [onSave]);
  useEffect(() => {
    if (viewOnly) return;
    if (hasChangesSinceLoad.current) debouncedSave();
    return () => debouncedSave.cancel();
  }, [nodes, edges, debouncedSave, viewOnly]);

  return (
    <Context.Provider value={{ apiUrl, accessToken, idApp, initialFlowId, flowInstanceId, viewOnly, isRunning, deleteNodeById, nodeDefinitions: nodeDefinition, nodesLoading, dynamicNodeTypes }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%', width: '100%', overflow: 'hidden', ...containerSx }} className="flow-editor-container">
        
        {showSidebar && <Sidebar onCollapse={() => setSidebarOpen(false)} />}

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', position: 'relative' }}>

          {/* ── appBar ĐÃ ĐƯỢC THIẾT KẾ LẠI (LIQUID GLASS MỚI) ── */}
          <AppBar 
            position="static" 
            elevation={0}
            sx={{ 
              backdropFilter: 'blur(16px)',
              background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.75)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              borderBottom: '1px solid', 
              borderColor: (theme) => alpha(theme.palette.divider, 0.5), 
              flexShrink: 0,
              px: 1
            }}
          >
            <Toolbar variant="dense" sx={{ gap: 1, minHeight: '48px !important', px: '4px !important', flexWrap: 'nowrap' }}>
              {!isLocked && !sidebarOpen && (
                <Tooltip title="Hiện thanh node">
                  <IconButton size="small" onClick={() => setSidebarOpen(true)}>
                    <ViewSidebarRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Vế Trái: Tiêu đề flow & Trạng thái hoạt động ứng dụng Liquid Glass iOS 26 */}
            <Box 
              sx={{ 
                minWidth: 0,
                maxWidth: 360,
                flexShrink: 1,
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.25,
                p: '3px 12px 3px 10px',
                borderRadius: '12px',
                background: (theme) => alpha(theme.palette.text.primary, 0.03),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.05),
                backdropFilter: 'blur(8px)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1)'
              }}
            >
              {/* Đèn báo trạng thái dạng Pulse tinh tế */}
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: (theme) => {
                      const colorKey = statusColor(flowStatus?.status);
                      // Kiểm tra an toàn bảo vệ hệ thống không bị crash khi dính trạng thái 'default'
                      return theme.palette[colorKey]?.main || theme.palette.text.disabled;
                    },
                    animation: isRunning ? 'liquidPulse 2s infinite ease-in-out' : 'none',
                    '@keyframes liquidPulse': {
                      '0%': { 
                        transform: 'scale(0.95)', 
                        boxShadow: (theme) => {
                          const colorKey = statusColor(flowStatus?.status);
                          const targetColor = theme.palette[colorKey]?.main || theme.palette.success.main;
                          return `0 0 0 0 ${alpha(targetColor, 0.5)}`;
                        }
                      },
                      '70%': { 
                        transform: 'scale(1)', 
                        boxShadow: (theme) => {
                          const colorKey = statusColor(flowStatus?.status);
                          const targetColor = theme.palette[colorKey]?.main || theme.palette.success.main;
                          return `0 0 0 6px ${alpha(targetColor, 0)}`;
                        }
                      },
                      '100%': { 
                        transform: 'scale(0.95)', 
                        boxShadow: '0 0 0 0 rgba(0,0,0,0)' 
                      }
                    }
                  }}
                />
              </Box>

              {/* Khối thông tin Title & Status */}
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={700} 
                  sx={{ 
                    letterSpacing: '-0.2px', 
                    color: 'text.primary',
                    lineHeight: 1.2,
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }} 
                >
                  {flowTitle || (flowId ? flowId : 'Quy trình chưa đặt tên')}
                </Typography>
                
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  {flowStatus?.status ? (
                    <Typography
                      variant="caption"
                      sx={{ 
                        fontSize: 10, 
                        fontWeight: 800,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        color: (theme) => {
                          const color = statusColor(flowStatus.status);
                          return color === 'default' ? 'text.secondary' : `${color}.main`;
                        }
                      }}
                    >
                      {flowStatus.status}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.5px', color: 'text.secondary' }}>
                      OFFLINE
                    </Typography>
                  )}

                  {/* Dấu chấm phân cách nhỏ nếu có trạng thái đồng bộ */}
                  {loading && !isInitialLoad.current && (
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                  )}
                  
                  {/* Trạng thái Syncing dịch chuyển mềm mại */}
                  {loading && !isInitialLoad.current && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CircularProgress 
                        size={8} 
                        thickness={7} 
                        sx={{ 
                          color: 'primary.main',
                          animationDuration: '550ms'
                        }} 
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 10, letterSpacing: '-0.1px' }}>
                        đang đồng bộ...
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>

              {/* Vế Giữa: khoảng trống co giãn. Thông báo lỗi đã tách xuống <FlowNotices> bên dưới. */}
              <Box sx={{ flex: 1, minWidth: 8 }} />

              {/* Vế Phải: Các nút điều khiển Action Bar dạng Pill-shaped */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <CanvasNodeSearch nodes={nodes} definitions={nodeDefinitionMap} onPick={focusNode} />
                {!viewOnly && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small" 
                      variant="contained" 
                      color="success"
                      startIcon={<PlayArrowRoundedIcon />}
                      onClick={handleStartFlow}
                      disabled={isRunning}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 700,
                        fontSize: 13,
                        borderRadius: '20px', // Pill-shape active state
                        px: 2,
                        height: 30,
                        boxShadow: (theme) => isRunning ? 'none' : `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                        '&:hover': {
                          boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.success.main, 0.4)}`,
                        }
                      }}
                    >
                      Kích hoạt
                    </Button>
                    
                    <Button
                      size="small" 
                      variant="text" 
                      color="error"
                      startIcon={<StopRoundedIcon />}
                      onClick={handleStopFlow}
                      disabled={!isRunning}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 700,
                        fontSize: 13,
                        borderRadius: '20px',
                        px: 2,
                        height: 30,
                        border: '1px solid',
                        borderColor: (theme) => isRunning ? alpha(theme.palette.error.main, 0.3) : 'transparent',
                        bgcolor: (theme) => isRunning ? alpha(theme.palette.error.main, 0.05) : 'transparent',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                          borderColor: 'error.main'
                        }
                      }}
                    >
                      Dừng
                    </Button>

                    <Tooltip title="Lưu lại cấu trúc (Ctrl+S)">
                      <IconButton 
                        size="small" 
                        onClick={onSave} 
                        color="primary"
                        sx={{ 
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                          border: '1px solid',
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                          p: '5px',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <SaveRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  
                  </Stack>
                )}

                <Tooltip title="Hướng dẫn sử dụng Flow (flow-guide: mô hình, cách kích hoạt, danh mục node, mẫu thiết kế)">
                  <IconButton
                    size="small"
                    onClick={() => setHuongDanOpen(true)}
                    color="primary"
                    sx={{
                      p: '5px',
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
                      transition: 'all 0.2s'
                    }}
                  >
                    <MenuBookRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Lịch sử chạy (mỗi lần kích hoạt, từng bước, lỗi)">
                  <IconButton
                    size="small"
                    onClick={() => setLichSuOpen(true)}
                    sx={{
                      p: '5px',
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.divider, 0.5),
                      transition: 'all 0.2s'
                    }}
                  >
                    <HistoryRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={pendingTaskCount > 0 ? `${pendingTaskCount} tác vụ nhập liệu đang chờ xử lý` : 'Danh sách tác vụ'}>
                  <IconButton 
                    size="small" 
                    onClick={() => setTaskPanelOpen(true)} 
                    color={pendingTaskCount > 0 ? 'warning' : 'default'}
                    sx={{ 
                      p: '5px',
                      border: '1px solid',
                      borderColor: (theme) => pendingTaskCount > 0 ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.divider, 0.5),
                      bgcolor: (theme) => pendingTaskCount > 0 ? alpha(theme.palette.warning.main, 0.08) : 'transparent',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Badge 
                      badgeContent={pendingTaskCount || 0} 
                      color="warning" 
                      max={99}
                      sx={{ 
                        '& .MuiBadge-badge': { 
                          fontWeight: 800, 
                          fontSize: 10,
                          boxShadow: '0 0 0 2px #fff'
                        } 
                      }}
                    >
                      <AssignmentRoundedIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Stack>

            </Toolbar>
          </AppBar>

          <FlowNotices notices={notices} />

          {/* Vùng chứa Canvas và NodeStatusBar */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            <Box sx={{ flex: 1, position: 'relative', minHeight: 100 }}>
              <div ref={reactFlowWrapper} style={{ height: '100%' }}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChangeHandler}
                  onEdgesChange={onEdgesChangeHandler}
                  onNodesDelete={onNodesDelete}
                  onEdgesDelete={onEdgesDelete}
                  onNodeClick={onNodeClick}
                  onNodeDoubleClick={onNodeDoubleClick}
                  onPaneClick={onPaneClick}
                  onConnect={onConnect}
                  nodeTypes={nodeTypes}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  fitView
                  snapToGrid
                  snapGrid={snapGrid}
                  defaultViewport={defaultViewport}
                  attributionPosition="bottom-right"
                  deleteKeyCode={['Backspace', 'Delete']}
                  nodesDraggable={!isLocked}
                  nodesConnectable={!isLocked}
                  elementsSelectable={true}
                >
                  <Controls showInteractive={!viewOnly} />
                  <Background />
                </ReactFlow>
              </div>
            </Box>

            {statusBarOpen && latestSelectedNode && !viewOnly && (
              <Box 
                sx={{ 
                  height: 260, 
                  borderTop: '1px solid', 
                  borderColor: 'divider', 
                  flexShrink: 0,
                  bgcolor: 'background.paper',
                  zIndex: 2 
                }}
              >
                <NodeStatusBar
                  selectedNode={latestSelectedNode}
                  onClose={() => setStatusBarOpen(false)}
                />
              </Box>
            )}
          </Box>
        </Box>

        {propertiesOpen && latestSelectedNode && !viewOnly && (
          <Box 
            sx={{ 
              width: 360, 
              height: '100%', 
              borderLeft: '1px solid', 
              borderColor: 'divider', 
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0
            }}
          >
            <PropertiesBar
              flowId={flowId}
              selectedNode={latestSelectedNode}
              nodeTypeMetadata={nodeTypeMetadata}
              onNodeDataChange={onNodeDataChange}
              isReadOnly={isRunning}
              onClose={() => setPropertiesOpen(false)} 
            />
          </Box>
        )}

        <Drawer
          type="drawer"
          anchor="right"
          open={taskPanelOpen}
          onClose={() => setTaskPanelOpen(false)}
          PaperProps={{ sx: { width: 360, display: 'flex', flexDirection: 'column' } }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <AssignmentRoundedIcon color="warning" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Yêu cầu nhập liệu
              {pendingTaskCount > 0 && (
                <Chip label={pendingTaskCount} size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: 11 }} />
              )}
            </Typography>
            <IconButton size="small" onClick={() => setTaskPanelOpen(false)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            <UserformTaskPanel
              onTaskCountChange={setPendingTaskCount}
              pollInterval={30000}
            />
          </Box>
        </Drawer>

        <HuongDanFlow open={huongDanOpen} onClose={() => setHuongDanOpen(false)} apiUrl={apiUrl} />

        <LichSuChay
          open={lichSuOpen}
          onClose={() => setLichSuOpen(false)}
          apiUrl={apiUrl}
          idApp={idApp}
          accessToken={accessToken}
          trustKey={trustKey}
          flowId={flowId}
          onFocusNode={(nodeId) => {
            // Chọn + đưa node lên giữa sơ đồ để đối chiếu bước trong lịch sử với node thật.
            setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === nodeId })));
            try { fitView({ nodes: [{ id: nodeId }], duration: 400, maxZoom: 1.2 }); } catch (e) { /* node đã bị xoá */ }
          }}
        />
      </Box>
    </Context.Provider>
  );
};

// ── Wrapper với ReactFlowProvider ─────────────────────────────────────────────
const FlowEditorWrapper = ({ initialFlowId, apiUrl, idApp, accessToken, trustKey, viewOnly }) => (
  <ReactFlowProvider>
    <FlowEditor
      initialFlowId={initialFlowId}
      apiUrl={apiUrl}
      idApp={idApp}
      accessToken={accessToken}
      trustKey={trustKey}
      viewOnly={viewOnly}
    />
  </ReactFlowProvider>
);

export default FlowEditorWrapper;